// Order of the hiring talent list (owner Hiring page and owner Network page).
//
//   1. Barbers with portfolio photos before barbers without. An owner deciding
//      who to approach can judge someone whose work they can see; a card with
//      no photos is a name and a city.
//   2. Inside each of those groups, most active first. The score comes from
//      talent_activity() (scripts/1064-talent-activity.sql): recency of the
//      barber's last sign-in, message, upload or network action, plus how
//      much of that they did in the last 30 days.
//   3. Ties go to the more complete profile, then the newer one.
//
// Until 1064 is run the activity map is null and the score falls back to the
// newest of the barber's last portfolio upload and their signup, which is the
// best "still here" signal the browser can read on its own.
//
// Pure on purpose (no Supabase import), so it is unit tested directly.

export type RankableBarber = {
  id: string
  created_at?: string | null
  portfolio: { created_at?: string | null }[]
  profiles?: { avatar_url?: string | null } | null
  city?: string | null
  years_experience?: number | null
  availability?: string[] | null
  payment_type_preference?: string | null
}

const DAY_MS = 86_400_000

/** 100 for today, ~37 at two weeks, ~1 at two months. Same curve as 1064. */
export function recencyScore(iso: string | null | undefined, now: number): number {
  if (!iso) return 0
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return 0
  const days = Math.max(0, (now - t) / DAY_MS)
  return Math.round(100 * Math.exp(-days / 14))
}

/** What the browser can tell on its own: last upload, else signup. */
export function fallbackActivity(b: RankableBarber, now: number): number {
  let latest: string | null = b.created_at ?? null
  for (const p of b.portfolio) {
    if (p.created_at && (!latest || p.created_at > latest)) latest = p.created_at
  }
  return recencyScore(latest, now)
}

export function completeness(b: RankableBarber): number {
  let score = 0
  if (b.profiles?.avatar_url) score += 2
  if (b.portfolio.length > 0) score += 2
  if (b.city) score += 1
  if (b.years_experience) score += 1
  if (b.availability && b.availability.length > 0) score += 1
  if (b.payment_type_preference) score += 1
  return score
}

export function rankTalent<T extends RankableBarber>(
  barbers: T[],
  activity: Map<string, number> | null,
  now: number = Date.now(),
): T[] {
  const scored = barbers.map((b) => ({
    b,
    photos: b.portfolio.length > 0 ? 1 : 0,
    // A barber the function did not score (hidden, unpublished) gets 0.
    active: activity ? (activity.get(b.id) ?? 0) : fallbackActivity(b, now),
    complete: completeness(b),
    created: b.created_at ? Date.parse(b.created_at) || 0 : 0,
  }))
  scored.sort(
    (x, y) =>
      y.photos - x.photos ||
      y.active - x.active ||
      y.complete - x.complete ||
      y.created - x.created ||
      (x.b.id < y.b.id ? -1 : x.b.id > y.b.id ? 1 : 0),
  )
  return scored.map((s) => s.b)
}
